import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FaCheck, FaExclamation, FaExclamationTriangle } from 'react-icons/fa';

import { propTypesLocalization } from '../../constants/Proptypes';

import Button from '../../../ui/Atoms/Button';
import Icon, { IconSize } from '../../../ui/Atoms/Icon';
import Modal from '../../../ui/Atoms/Modal';
import Newsletter from '../../../ui/Molecules/Newsletter';
import NewsletterCard from '../../../ui/Molecules/NewsletterCard';

import { createBrevoContact, identifyBrevoContact } from '../../services/brevo';

import { LowercaseLanguageCode } from '../../constants/Enums';
import * as S from './NewsletterSection.styles';

export const NewsletterSection = ({
  customNewsletter,
  localization,
  isSideBlock,
  isFullScreen,
}) => {
  const [inputBasicValue, setInputBasicValue] = useState('');
  const [subscription, setSubscriptionMessage] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalIcon, setModalIcon] = useState(<FaExclamation />);
  const [isCustom, setIsCustom] = useState(!!customListId);
  const [didConsent, setDidConsent] = useState(false);

  useEffect(() => {
    setIsCustom(!!customListId);
  }, [isCustom]);

  if (!localization) {
    return null;
  }

  const {
    sendinblue_list_id: customListId,
    custom_title: customTitle,
    custom_description: customDescription,
    custom_illustration: customIllustration = {},
    is_wooflash: isWooflash,
  } = customNewsletter || '';

  const { data: local, lang } = localization || {};
  const {
    newsletter_title_side: newsletterTitleSide,
    newsletter_title: newsletterTitleBottom,
    newsletter_conditions: newsletterConditions,
    newsletter_description_side: newsletterDescriptionSide,
    newsletter_description: newsletterDescriptionBottom,
    newsletter_placeholder: newsletterPlaceholder,
    newsletter_subscription_button: newsletterLabel,
    newsletter_validation_button: newsletterButton,
    newsletter_validation_message: newsletterMessage,
    newsletter_error_message: newsletterErrorMessage,
    newsletter_already_sub: newsletterAlreadySub,
    newsletter_invalid_email: newsletterInvalidEmail,
  } = local || '';

  const newsletterTitle = isSideBlock
    ? newsletterTitleSide
    : newsletterTitleBottom;

  const newsletterDescription = isSideBlock
    ? newsletterDescriptionSide
    : newsletterDescriptionBottom;

  const createContact = async (event, email) => {
    event.preventDefault();

    setDidConsent(false);
    setInputBasicValue('');

    switch (
      await createBrevoContact({
        email,
        lang: lang?.substring(0, 2) || LowercaseLanguageCode.EN,
        customListId: parseInt(customListId),
        product: isWooflash ? 'wooflash' : 'wooclap',
      })
    ) {
      case 201:
        setSubscriptionMessage(newsletterMessage);
        setModalIcon(<FaCheck />);
        break;
      case 204:
        setSubscriptionMessage(
          customListId ? newsletterMessage : newsletterAlreadySub
        );
        setModalIcon(customListId ? <FaCheck /> : <FaExclamation />);
        break;
      case 400:
        setSubscriptionMessage(newsletterInvalidEmail);
        setModalIcon(<FaExclamationTriangle />);
        break;
      default:
        setSubscriptionMessage(newsletterErrorMessage);
        setModalIcon(<FaExclamation />);
        break;
    }

    identifyBrevoContact({ email });
    setIsModalVisible(true);
  };

  return (
    <S.NewsletterSection
      isCustom={isCustom}
      isFullScreen={isFullScreen}
      isSideBlock={isSideBlock}
    >
      {isSideBlock ? (
        <NewsletterCard
          description={newsletterDescription}
          didConsent={didConsent}
          onAccept={() => setDidConsent(!didConsent)}
          label={newsletterLabel}
          optinLabel={newsletterConditions}
          onChange={event => setInputBasicValue(event?.target?.value)}
          onClick={event => createContact(event, inputBasicValue)}
          placeholder={newsletterPlaceholder}
          title={newsletterTitle}
          value={inputBasicValue}
        />
      ) : (
        <Newsletter
          illustration={customIllustration}
          description={customDescription || newsletterDescription}
          didConsent={didConsent}
          isFullScreen={isFullScreen}
          label={newsletterLabel}
          optinLabel={newsletterConditions}
          onAccept={() => setDidConsent(!didConsent)}
          onChange={event => setInputBasicValue(event?.target?.value)}
          onClick={event => createContact(event, inputBasicValue)}
          placeholder={newsletterPlaceholder}
          title={customTitle || newsletterTitle}
          value={inputBasicValue}
          theme={isWooflash ? 'wooflash' : 'wooclap'}
        />
      )}
      {isModalVisible && (
        <Modal onModalClose={() => setIsModalVisible(false)}>
          <S.ModalContent>
            <Icon size={IconSize.BIG}>{modalIcon}</Icon>
            <S.ModalTitle>{subscription}</S.ModalTitle>
            <Button onClick={() => setIsModalVisible(false)}>
              {newsletterButton}
            </Button>
          </S.ModalContent>
        </Modal>
      )}
    </S.NewsletterSection>
  );
};

NewsletterSection.propTypes = {
  /**
   * Custom object allowing to update the newsletter page copy
   * depending of the Sendinblue list used in the query params.
   */
  customNewsletter: PropTypes.shape({
    sendinblue_list_id: PropTypes.string,
    custom_title: PropTypes.string,
    custom_description: PropTypes.string,
    is_wooflash: PropTypes.bool,
  }),
  /**
   * Set a larger height to the Newsletter component if true.
   */
  isFullScreen: PropTypes.bool,
  /**
   * Display the side block version if true, else display the large one.
   */
  isSideBlock: PropTypes.bool,
  /**
   * Localization document.
   */
  localization: PropTypes.shape(propTypesLocalization),
};
